// Decode a sefaria.org URL (or site-relative path) into a canonical ref -
// the resolver behind in-text citation pills. Pure, DOM-free, network-free.
//
// ACCURACY-LOCK POSTURE: decoding is STRUCTURAL only - the output must parse
// through parseCanonicalRef (normalizeRef.ts, the ref-validation source of
// truth; never reimplemented here). Decoding does NOT make a citation real:
// the navigator additionally gates every decoded ref against the current
// source's real getLinks result before any in-app behavior is wired. An
// undecodable or ungated href keeps its hardened external/inert behavior.
//
// Accepted forms (both produced by Sefaria and by our own sefariaUrl()):
//   https://www.sefaria.org/Chagigah.12a.3?lang=he   (dots form)
//   /Rashi_on_Genesis.1.1.2                          (relative, underscores)
//   https://www.sefaria.org/Menachot%2043b%3A5       (encoded canonical form)

import { parseCanonicalRef } from "./normalizeRef.ts";

const SEFARIA_HOST = /(^|\.)sefaria\.org$/i;

export function refFromSefariaUrl(href: string): string | null {
  let path: string;
  if (/^https?:/i.test(href)) {
    let url: URL;
    try {
      url = new URL(href);
    } catch {
      return null;
    }
    if (url.protocol !== "https:" || !SEFARIA_HOST.test(url.hostname)) return null;
    path = url.pathname;
  } else if (href.startsWith("/")) {
    path = href.split(/[?#]/)[0];
  } else {
    return null;
  }

  path = path.replace(/^\/+/, "").replace(/\/+$/, "");
  // Multi-segment paths (api/..., texts/..., sheets/...) are site pages, not refs.
  if (!path || path.includes("/")) return null;

  let raw: string;
  try {
    raw = decodeURIComponent(path);
  } catch {
    return null;
  }
  raw = raw.replace(/_/g, " ").trim();
  if (!raw) return null;

  // Dots form -> canonical: the numeric tail's dots become colons, the first
  // title/tail dot becomes a space ("Chagigah.12a.3" -> "Chagigah 12a:3",
  // "Rashi on Genesis.1.1.2" -> "Rashi on Genesis 1:1:2", ranges included).
  let ref = raw;
  const m = raw.match(/^(.+?)\.(\d+[ab]?(?:\.\d+)*(?:-\d+(?:\.\d+)*)?)$/);
  if (m) ref = `${m[1].trim()} ${m[2].replace(/\./g, ":")}`;

  // Structural validation through the shared parser - null when it is not a
  // parseable canonical ref (e.g. an amud-less daf ref, a topics page).
  return parseCanonicalRef(ref) ? ref : null;
}

// Do two canonical refs refer to the same passage at different precision?
// Exact match, or one extends the other by a ":"-led segment ("Menachot 43b"
// vs "Menachot 43b:5"). The colon boundary keeps "Genesis 1" from swallowing
// "Genesis 10". Pure string logic - the caller supplies only validated refs.
export function refsOverlap(a: string, b: string): boolean {
  if (a === b) return true;
  return a.startsWith(`${b}:`) || b.startsWith(`${a}:`);
}
